import axios from "axios";
import ToastEventBus from "primevue/toasteventbus";
import AppEventBus from "@/AppEventBus";
import values from "../settings";
import { DOWNLOAD_TIMEOUT_MS, DOWNLOAD_TIMEOUT_MESSAGE } from "./fileDownload";
import { localISODate } from "./dates";

axios.defaults.withCredentials = true;
axios.defaults.baseURL = (values.env == "PROD") ? values.prod_serverUrl : values.dev_serverUrl;

// Fallback (best-effort) de la propagación sin logout: el backend estampa
// `X-Entitlements-Version` en cada respuesta autenticada. Si la versión sube y el push por
// WebSocket no llegó (socket caído, mensaje perdido), App.vue re-fetchea la sesión.
// Cross-origin requiere que el backend exponga el header (CORS_EXPOSE_HEADERS).
let lastEntitlementsVersion = null;
// Sincroniza la versión vista desde fuera del interceptor (p.ej. el push por WS) para no
// disparar un refresh duplicado en la siguiente respuesta que traiga ese mismo header.
export function noteEntitlementsVersion(version) {
    const v = parseInt(version, 10);
    if (!Number.isNaN(v) && (lastEntitlementsVersion === null || v > lastEntitlementsVersion)) {
        lastEntitlementsVersion = v;
    }
}

// Manejo central del 403 (autorización por permisos anclada al usuario).
// El backend responde HTTP 200 con body.status === 403 cuando el usuario está
// autenticado pero no tiene el permiso requerido. Aquí lo interceptamos una sola
// vez para TODAS las llamadas y mostramos un toast estándar (group "global",
// montado en AppWrapper.vue), sin tener que tocar cada componente.
axios.interceptors.response.use(
    (response) => {
        if (response && response.data && response.data.status === 403) {
            ToastEventBus.emit("add", {
                severity: "warn",
                summary: "Acceso denegado",
                detail: response.data.error || "No tienes permiso para realizar esta acción.",
                life: 5000,
                group: "global",
            });
        }
        // Fallback por header: detectar un aumento de versión de entitlements.
        const header = response && response.headers && response.headers["x-entitlements-version"];
        if (header !== undefined && header !== null && header !== "") {
            const v = parseInt(header, 10);
            if (!Number.isNaN(v)) {
                if (lastEntitlementsVersion === null) {
                    lastEntitlementsVersion = v; // baseline: primera respuesta vista, sin refrescar
                } else if (v > lastEntitlementsVersion) {
                    lastEntitlementsVersion = v;
                    AppEventBus.emit("entitlements_changed", { entitlementsVersion: v });
                }
            }
        }
        return response;
    },
    (error) => Promise.reject(error)
);

// Descargas (PDF, XML, Excel, ZIP): un solo camino para los 11 helpers de abajo.
// Devuelve el Blob de la respuesta, o `{status: 404, error}` si el request falló.
// Con timeout: sin él, una respuesta que nunca llega dejaba el botón girando para
// siempre y sin ninguna señal (reporte del 2026-10-07 con una proforma). Qué hacer
// con el resultado lo decide saveDownload (service/fileDownload.js).
async function downloadBlob(url, data) {
    return axios
        .post(url, JSON.stringify(data), {responseType: 'blob', timeout: DOWNLOAD_TIMEOUT_MS})
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            const timedOut = !!(error && error.code === 'ECONNABORTED');
            return timedOut ? {status: 404, error: DOWNLOAD_TIMEOUT_MESSAGE} : {status: 404};
        });
}

// Account
async function syncAccount() {
    const response = await axios
        .post(
            '/dashboard/account/sync',
            {},   
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Account Metrics
async function getBusinessMetrics() {
    const response = await axios
        .post(
            '/dashboard/account/metrics/get',
            {},   
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function getFinantialStateData(timeFilter) {
    const response = await axios
        .post(
            '/dashboard/account/metrics/finantial-data/get',
            {filter: timeFilter},   
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function getGrossMarginData(timeFilter) {
    const response = await axios
        .post(
            '/dashboard/account/metrics/gross-margin/get',
            {filter: timeFilter},
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function getRecentSales() {
    const response = await axios
        .post(
            '/dashboard/account/metrics/recent-sales/get',
            {},
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function getTopProducts(days) {
    const response = await axios
        .post(
            '/dashboard/account/metrics/top-products/get',
            {days: days},
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function getReceivablesPayables() {
    const response = await axios
        .post(
            '/dashboard/account/metrics/receivables-payables/get',
            {},
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function getReceivablesPayablesDetail() {
    // Detalle documento-por-documento de la cartera (CxC/CxP) + agregados de
    // cabecera. Snapshot de saldos vivos (no depende de un periodo).
    const response = await axios
        .post(
            '/dashboard/account/receivables-payables/detail/get',
            {},
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Authentication
async function login(email, password) {
    const data = {
        email: email,
        password: password
    };
    
    const response = await axios
        .post('/dashboard/auth/sign-in', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}
async function logout() {
    const data = {};
    const response = await axios
        .post('/dashboard/auth/sign-out', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}
// Rehidrata la sesión desde la cookie al cargar la app (refresh o llegada del registro web).
// El backend responde 502 (@requireAuth) cuando no hay sesión; lo mapeamos a {status: 502}.
async function getSession() {
    const response = await axios
        .post('/dashboard/auth/session', {})
        .then(response => { return response.data; })
        .catch(() => { return {status: 502} })
    return response
}
// Fase 5 (capa PRESENTED): documento de perfil de pantalla fusionado y filtrado por entitlement
// (system → vertical → plataforma → cuenta). Body: {screenId, platform}. Read-only.
async function getScreenProfile(screenId, platform = 'web') {
    const response = await axios
        .post('/dashboard/screen-profile/get', {screenId, platform})
        .then(response => { return response.data; })
        .catch(() => { return {status: 502} })
    return response
}
// Fase 4 (asientos / sub-usuarios). Todos requieren el permiso 'configuration' (el dueño).
async function listSubUsers() {
    const response = await axios
        .post('/dashboard/sub-user/get-all', {})
        .then(response => { return response.data; })
        .catch(() => { return {status: 502} })
    return response
}
// Horarios de trabajo (F5 del cliente móvil): cada respuesta trae la pantalla completa.
async function postWorkSchedules(path, payload = {}) {
    const response = await axios
        .post(path, payload)
        .then(response => { return response.data; })
        .catch(error => { return (error.response && error.response.data) || {status: 500}; })
    return response
}
function getWorkSchedules() { return postWorkSchedules('/dashboard/horarios/get'); }
function saveWorkSchedule(schedule) { return postWorkSchedules('/dashboard/horarios/save', schedule); }
function deleteWorkSchedule(id) { return postWorkSchedules('/dashboard/horarios/delete', {id}); }
function assignWorkSchedule(change) { return postWorkSchedules('/dashboard/horarios/user/assign', change); }
function saveScheduleException(exception) { return postWorkSchedules('/dashboard/horarios/exception/save', exception); }
function deleteScheduleException(id) { return postWorkSchedules('/dashboard/horarios/exception/delete', {id}); }
async function createSubUser(username, password, permissionSelection = null, role = 'Operador') {
    const response = await axios
        .post('/dashboard/sub-user/create', {username, password, permissionSelection, role})
        .then(response => { return response.data; })
        .catch(error => { return (error.response && error.response.data) || {status: 500}; })
    return response
}
async function updateSubUser(userId, changes) {
    // changes: {permissionSelection?, suspended?}
    const response = await axios
        .post('/dashboard/sub-user/update', {userId, ...changes})
        .then(response => { return response.data; })
        .catch(error => { return (error.response && error.response.data) || {status: 500}; })
    return response
}
async function updatePassword(oldPass, newPass) {
    const data = {old: oldPass, new: newPass};
    const response = await axios
        .post('/dashboard/auth/password/update', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}
async function requestPasswordReset(email) {
    const data = {email: email};
    const response = await axios
        .post('/dashboard/auth/password/reset/request', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}
async function confirmPasswordReset(uid, token, password) {
    const data = {uid: uid, token: token, password: password};
    const response = await axios
        .post('/dashboard/auth/password/reset/confirm', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Business
async function getBusiness() {
    const response = await axios
        .post('/dashboard/business/get')
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function saveBusiness(form) {
    const response = await axios
        .post(
            '/dashboard/business/save',
            form,   
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function getEconActivities() {
    const response = await axios
        .post('/dashboard/business/activity/get-all')
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function saveEconActivity(form) {
    const response = await axios
        .post(
            '/dashboard/business/activity/save',
            form,   
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Establecimientos y puntos de emisión de Negocio. Todos devuelven el mismo payload que
// getEconActivities (actividades, establecimientos con sus puntos y el cupo).
async function postBusinessForm(url, fields) {
    const form = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
        if (value !== null && value !== undefined) { form.append(key, value) }
    });
    return axios
        .post(url, form)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
}

function saveEstablishment(fields) {
    return postBusinessForm('/dashboard/business/establishment/save', fields);
}

function deleteEstablishment(id) {
    return postBusinessForm('/dashboard/business/establishment/delete', {id: id});
}

function saveEmitionPoint(fields) {
    return postBusinessForm('/dashboard/business/emition-point/save', fields);
}

function deleteEmitionPoint(id) {
    return postBusinessForm('/dashboard/business/emition-point/delete', {id: id});
}

async function setDefaultEconActivity(id) {
    const response = await axios
        .post(
            '/dashboard/business/activity/default/save',
            {id: id},   
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// DEPRECIATED
async function saveDefaults(defaults) {
    const data = {defaults: defaults};
    const response = await axios
        .post('/dashboard/business/defaults/save', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function saveUserSettings(settings) {
    const data = {settings: settings};
    const response = await axios
        .post('/dashboard/user/settings/save', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Services
async function getServicePlans() {
    const response = await axios
        .post(
            '/dashboard/services/get-all',
            {},
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}
// Catálogo comercial vigente desde DB (Plan/PlanVersion) — grilla de renovación (Fase 3).
async function getPlanCatalogue() {
    const response = await axios
        .post(
            '/dashboard/subscription/catalogue',
            {},
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Subscription
async function getSubscriptions() {
    const response = await axios
        .post(
            '/dashboard/subscription/get-all',
            {},   
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}
async function renewSubscription(form) {
    const response = await axios
        .post(
            '/dashboard/subscription/renew',
            form,   
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Device
async function getDevices() {
    const response = await axios
        .post(
            '/dashboard/device/get-all',
            {},   
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function selectDevice(id) {
    const response = await axios
        .post(
            '/dashboard/device/select',
            {deviceId: id},   
        )
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Banks
async function getBanks() {
    const response = await axios
        .post('/dashboard/bank/get-all')
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Pasarelas de pago aceptadas (catálogo del core: no depende del módulo contable,
// una cuenta CARD registra cobros con contabilidad apagada).
async function getPaymentGateways() {
    const response = await axios
        .post('/dashboard/payment-gateway/get-all')
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Finantial Accounts
async function getFinantialAccounts() {
    const response = await axios
        .post('/dashboard/finantial-account/get-all')
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function getFinantialAccount(id, page = 1, pageSize = 50, initialDate = null, endDate = null) {
    const data = {
        id: id,
        page: page,
        pageSize: pageSize
    };
    
    // Agregar fechas solo si no son null, formateando a ISO string (YYYY-MM-DD)
    if (initialDate) {
        // Fecha local, no UTC: de noche toISOString() ya es «mañana» (H64).
        data.initialDate = localISODate(initialDate);
    }
    if (endDate) {
        data.endDate = localISODate(endDate);
    }
    
    const response = await axios
        .post('/dashboard/finantial-account/get', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function saveFinantialAccount(finantialAccount) {
    const response = await axios
        .post('/dashboard/finantial-account/save', JSON.stringify(finantialAccount))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function deleteFinantialAccount(id) {
    const data = {id: id};
    // El cuerpo, no el status HTTP: el backend responde 200 HTTP con {status: 422, error}
    // cuando la cuenta tiene movimientos, y la pantalla la daba por eliminada (H56).
    const response = await axios
        .post('/dashboard/finantial-account/delete', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function createMultipleTransactions(transactions) {
    // Atomic batch: a single multipart POST. Either every transaction is
    // persisted (and balances updated) or none of them is. This replaces the
    // legacy Promise.all pattern that could leave the books out of sync if
    // one of the N requests failed mid-way.
    if (!transactions || transactions.length === 0) {
        return {status: 400, error: "No transactions to send"};
    }

    // Resolve the shared document target from the first row. The backend
    // also enforces all rows share the same saleId/purchaseId. Free standing
    // account movements (no sale/purchase) are also supported when neither id
    // is provided.
    const first = transactions[0];
    const saleId = first.saleId || null;
    const purchaseId = first.purchaseId || null;

    const form = new FormData();
    if (saleId) form.append("saleId", saleId);
    if (purchaseId) form.append("purchaseId", purchaseId);

    const payload = transactions.map((t, i) => {
        const item = {
            accountId: t.accountId,
            amount: t.amount,
            reference: t.reference,
            tip: t.tip || 0,
        };
        if (t.paymentMethod) item.paymentMethod = t.paymentMethod;
        if (t.approvalNumber) item.approvalNumber = t.approvalNumber;
        if (t.proof) {
            const key = `proof_${i}`;
            form.append(key, t.proof);
            item.proofKey = key;
        }
        return item;
    });
    form.append("transactions", JSON.stringify(payload));

    try {
        const response = await axios.post(
            '/dashboard/finantial-account/transaction/create-batch',
            form
        );
        const body = response.data || {};
        if (body.status === 200) {
            const created = Array.isArray(body.data) ? body.data : [];
            return {
                status: 200,
                data: {
                    transactions: created,
                    transactionIds: created.map(t => t.id),
                    count: created.length,
                }
            };
        }
        // El motivo del backend (422) llega al aviso del formulario (H36).
        return {status: body.status || 500, error: body.error || "Batch failed"};
    } catch (error) {
        console.error("Error creating multiple transactions:", error);
        return {status: 500, error: error.message};
    }
}

async function registerPayment({ saleId = null, purchaseId = null, amount = null, accountId = null, paymentMethod = null, approvalNumber = null, proof = null, creditNoteId = null }) {
    // Abono (pago parcial) sobre una venta o compra. El backend crea el
    // Payment (+ FinantialAccountTransaction si viene accountId) de forma
    // atómica y marca el documento como PAID cuando el saldo llega a cero.
    // Devuelve el documento serializado completo (con payments y saldos).
    const form = new FormData();
    if (saleId) form.append("saleId", saleId);
    if (purchaseId) form.append("purchaseId", purchaseId);
    // Canje de nota de crédito: el monto lo fija la nota (se usa entera).
    if (creditNoteId) form.append("creditNoteId", creditNoteId);
    if (amount !== null && amount !== undefined) form.append("amount", amount);
    if (accountId) form.append("accountId", accountId);
    if (paymentMethod) form.append("paymentMethod", paymentMethod);
    if (approvalNumber) form.append("approvalNumber", approvalNumber);
    if (proof) form.append("proof", proof);

    try {
        const response = await axios.post('/dashboard/payment/register', form);
        return response.data || {status: 500};
    } catch (error) {
        console.error("Error registering payment:", error);
        return {status: 500, error: error.message};
    }
}

async function registerBulkPayment({ type, allocations, accountId = null, paymentMethod = null, approvalNumber = null, proof = null }) {
    // Cobro/pago EN LOTE: reparte un monto entre varias ventas/compras.
    // `allocations` = [{documentId, amount}]. El backend aplica todo en una
    // transacción atómica (todo o nada) y marca PAID los que se salden.
    const form = new FormData();
    form.append("type", type);
    form.append("allocations", JSON.stringify(allocations));
    if (accountId) form.append("accountId", accountId);
    if (paymentMethod) form.append("paymentMethod", paymentMethod);
    if (approvalNumber) form.append("approvalNumber", approvalNumber);
    if (proof) form.append("proof", proof);

    try {
        const response = await axios.post('/dashboard/payment/register-bulk', form);
        return response.data || {status: 500};
    } catch (error) {
        console.error("Error registering bulk payment:", error);
        return {status: 500, error: error.message};
    }
}

async function getFinantialAccountTransaction(id = null, saleId = null, purchaseId = null) {
    const data = {};
    if (id) {  data.id = id; }
    if (saleId) {  data.saleId = saleId; }
    if (purchaseId) {  data.purchaseId = purchaseId; }
    
    const response = await axios
        .post('/dashboard/finantial-account/transaction/get', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Message & Broadcast
async function getBroadcastMessage() {
    const data = {};
    const response = await axios
        .post('/dashboard/broadcast/get', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Historial de broadcasts del negocio para el centro de notificaciones (campanita).
async function getBroadcastMessages() {
    const response = await axios
        .post('/dashboard/broadcast/list', {})
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function seenBroadcastMessage(messageId) {
    const data = {messageId: messageId};
    const response = await axios
        .post('/dashboard/broadcast/seen', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Customer
async function getCustomers(filter=null) {
    var data = {};
    if (filter !== null) { data["filter"] = filter }

    const response = await axios
        .post('/dashboard/customer/get-all', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function saveCustomer(customer) {
    const response = await axios
        .post('/dashboard/customer/save', JSON.stringify(customer))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function deleteCustomers(ids) {
    const data = {ids: ids};
    const response = await axios
        .post('/dashboard/customer/delete', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function downloadClients() {
    return downloadBlob('/dashboard/customer/download', {});
}

// Product
// `sellableOnly`: la venta y la proforma no ofrecen insumos (pan, sal, jabón).
async function getProducts(filter=null, filterType=null, provider=true, sellableOnly=false) {
    var data = {provider: provider};
    if (sellableOnly) { data["sellableOnly"] = true }
    if (filter !== null) { data["filter"] = filter }
    else if (filterType !== null) { data["filterType"] = filterType }
    const response = await axios
        .post('/dashboard/product/get-all', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function saveProduct(product) {
    const response = await axios
        .post('/dashboard/product/save', JSON.stringify(product))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Categorias de producto: la dimension sobre la que Contabilidad abre
// Ventas / Inventario / Costo de ventas en varias cuentas.
async function getProductCategories() {
    const response = await axios
        .post('/dashboard/product/category/get-all', JSON.stringify({}))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function saveProductCategory(category) {
    const response = await axios
        .post('/dashboard/product/category/save', JSON.stringify(category))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function deleteProducts(ids) {
    const data = {ids: ids};
    const response = await axios
        .post('/dashboard/product/delete', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function downloadProducts() {
    return downloadBlob('/dashboard/product/download', {});
}

async function getProductSalesReport(initialDate, finalDate) {
    const data = {initialDate: initialDate, finalDate: finalDate};
    const response = await axios
        .post('/dashboard/product/report/sale', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })

    return response
}

async function getProductKardex(productId, initialDate = null, finalDate = null, limit = null) {
    const data = {productId: productId};
    if (initialDate !== null) { data['startDate'] = initialDate; }
    if (finalDate !== null) { data['endDate'] = finalDate; }
    if (limit !== null) { data['limit'] = limit; }
    const response = await axios
        .post('/dashboard/product/kardex', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })

    return response
}

async function generateProductKardexReport(startDate, endDate, email, reportType) {
    const data = {
        startDate: startDate,
        endDate: endDate,
        email: email,
        reportType: reportType
    };
    const response = await axios
        .post('/dashboard/product/kardex/report', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })

    return response
}

async function saveProductStockAdjustment(productId, adjustmentType, quantity, reason = '', reasonCode = null) {
    const data = {
        productId: productId,
        adjustmentType: adjustmentType,
        quantity: quantity,
        reason: reason,
        reasonCode: reasonCode
    };
    const response = await axios
        .post('/dashboard/product/stock-adjustment/save', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })

    return response
}

async function getProductCompositions(productId) {
    const response = await axios
        .post('/dashboard/product/composition/get-all', JSON.stringify({ subProductId: productId }))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function saveProductCompositions(subProductId, compositions) {
    const data = {
        subProductId: subProductId,
        compositions: compositions
    };
    const response = await axios
        .post('/dashboard/product/composition/save', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return error.response || {status: 404}
        })

    return response
}

async function deleteProductComposition(compositionId) {
    const response = await axios
        .post('/dashboard/product/composition/delete', JSON.stringify({ id: compositionId }))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Provider
async function getProviders(lite=false, filter=null) {
    var data = {lite: lite};
    if (filter !== null) { data['filter'] = filter }
    const response = await axios
        .post('/dashboard/product/provider/get-all', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function saveProvider(provider) {
    const response = await axios
        .post('/dashboard/product/provider/save', JSON.stringify(provider))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function deleteProviders(ids) {
    const data = {ids: ids};
    const response = await axios
        .post('/dashboard/product/provider/delete', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Balance
async function getBalance(initialDate, finalDate, searchType) {
    const data = {
        initialDate: initialDate,
        finalDate: finalDate,
        searchType: searchType
    }
    
    const response = await axios
        .post('/dashboard/balance/get', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function printBalance(data) {
    const response = await axios
        .post('/dashboard/balance/print', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Proforma
async function getProformas(client=null) {
    var data = {};
    if (client!==null && client !== "") { data["client"] = client }

    const response = await axios
        .post('/dashboard/proforma/get-all', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function getProforma(proformaId) {
    const data = {proformaId: proformaId};
    const response = await axios
        .post('/dashboard/proforma/get', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function saveProforma(proforma) {
    var data = {...proforma};
    data = JSON.stringify(data);
    
    const response = await axios
        .post('/dashboard/proforma/save', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function sendProformaEmail(proformaId, emails) {
    const data = {proformaId: proformaId, emails: emails};
    const response  = await axios
        .post('/dashboard/proforma/send', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error =>{ 
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function downloadProformaFile(proformaId) {
    return downloadBlob('/dashboard/proforma/file/download', {proformaId: proformaId});
}

async function deleteProforma(id) {
    const data = {proformaId: id};
    const response = await axios
        .post('/dashboard/proforma/delete', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Documents
async function signPdfDocument(form) {
    const response = await axios
        .post('/dashboard/document/sign-and-send', form)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Sale
async function getSales(client=null, status=null, start=null, end=null, amount=null, collected=null, receipt=null, creditable=null) {
    var data = {};
    if (client!==null && client !== "") { data["client"] = client }
    if (status!==null) { data["status"] = status }
    if (start!==null) { data["start"] = start }
    if (end!==null) { data["end"] = end }
    if (amount!==null && amount!== "") { data["amount"] = amount }
    if (collected!==null) { data["collected"] = collected }
    if (receipt!==null) { data["receipt"] = receipt }
    // Selector de la N/C: facturas vigentes que todavía tienen algo por acreditar.
    if (creditable!==null) { data["creditable"] = creditable }

    const response = await axios
        .post('/dashboard/sale/get-all', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Una página de la lista de Ventas: {items, page, pageSize, totalPages, totalItems}.
// `params`: page, pageSize, sortField, sortOrder, search, client, status, collected, start, end.
async function getSalesPage(params) {
    const data = {};
    for (const [key, value] of Object.entries(params)) {
        if (value !== null && value !== undefined && value !== "") { data[key] = value }
    }

    const response = await axios
        .post('/dashboard/sale/get-all', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// `creditable` agrega a cada ítem lo que queda por acreditar (formulario de N/C).
async function getSale(saleId, creditable=false) {
    const data = {saleId: saleId};
    if (creditable) { data["creditable"] = true }
    const response = await axios
        .post('/dashboard/sale/get', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function saveSale(sale, receipt) {
    var data = {...sale};
    if (receipt === true) { data["receipt"] = "True" }
    data = JSON.stringify(data);
    
    const response = await axios
        .post('/dashboard/sale/save', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function collectSale(saleId, tip=0) {
    const data = JSON.stringify({id: saleId, tip: tip});
    const response = await axios
        .post('/dashboard/sale/collect', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function printSale(id) {
    const data = {saleId: id};
    const response = await axios
        .post('/dashboard/sale/print', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Pregunta AHORA al SRI si una solicitud de anulacion ya prospero, sin esperar al
// barrido automatico. Puede responder de dos formas: con el edoc ya resuelto (si
// bastó el web service del SRI) o con {async: true}, y en ese caso el resultado
// llega por WebSocket como 'annulment_result'.
async function checkAnnulmentStatus(edocId) {
    const data = {edocId: edocId};
    const response = await axios
        .post('/dashboard/edocument/annulment/check', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// `reportToSri=false` anula SOLO en Contablus: el SRI seguira dando el
// comprobante por vigente. Unicamente cuando el usuario lo elige a sabiendas,
// porque el negocio no tiene credenciales del portal cargadas.
// Informa al SRI una anulacion que se hizo solo en Contablus (quedo en estado
// 'unreported' porque en su momento no habia credenciales del portal).
async function reportAnnulmentToSri(edocId) {
    const data = {edocId: edocId};
    const response = await axios
        .post('/dashboard/edocument/annulment/report', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function deleteSale(id, reportToSri = true) {
    const data = {saleId: id, reportToSri: reportToSri};
    const response = await axios
        .post('/dashboard/sale/delete', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Resumen de IVA por tarifa del periodo (insumo del formulario 104). Junta
// ventas y compras, por eso vive en su propio endpoint y no en downloadSales.
async function downloadTaxSummary(initialDate, endDate) {
    return downloadBlob('/dashboard/tax/summary', {initialDate: initialDate, endDate: endDate});
}

async function downloadSales(format, saleType, initialDate, endDate) {
    return downloadBlob('/dashboard/sale/download-report', {format: format, saleType: saleType, initialDate: initialDate, endDate: endDate});
}

async function downloadSaleEdoc(edocId, type) {
    return downloadBlob('/dashboard/sale/download-edoc', {edocId: edocId, type: type});
}

async function downloadSalePDF(saleId, type) {
    return downloadBlob('/dashboard/sale/download-edoc', {saleId: saleId, type: type});
}

// ============================================================
// Restaurante (Mesas) — módulo ventas_restaurante
// Capa web sobre backend.services.restaurant (misma lógica que el móvil). Gateado por
// 'restaurantTables' (mesero) y 'restaurantTablesManage' (config del salón, admin).
// ============================================================
async function _restaurantPost(url, body) {
    const response = await axios
        .post(url, JSON.stringify(body || {}))
        .then(r => r.data)
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        });
    return response;
}

function getRestaurantFloor(zone = null) {
    return _restaurantPost('/dashboard/restaurant/floor', zone ? {zone} : {});
}
function getRestaurantTableDetail(tableId) {
    return _restaurantPost('/dashboard/restaurant/table', {tableId});
}
function openRestaurantAccount(tableId, label, items) {
    return _restaurantPost('/dashboard/restaurant/account/open', {tableId, label, items});
}
function addRestaurantItems(saleId, items) {
    return _restaurantPost('/dashboard/restaurant/account/items/add', {saleId, items});
}
function removeRestaurantItem(saleId, itemId) {
    return _restaurantPost('/dashboard/restaurant/account/items/remove', {saleId, itemId});
}
function sendRestaurantToKitchen(saleId) {
    return _restaurantPost('/dashboard/restaurant/account/kitchen', {saleId});
}
// Un pago en multipart, para poder llevar la foto del voucher / comprobante (`proof`, un File ya
// comprimido); `accountId` = cuenta de tesorería, `approvalNumber` = voucher de tarjeta, `tip` = propina.
async function _restaurantPaymentPost(url, fields, proof) {
    const form = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') form.append(key, value);
    });
    if (proof) form.append('proof', proof);
    try {
        const response = await axios.post(url, form);
        return response.data || {status: 500};
    } catch (error) {
        console.error("Error registering restaurant payment:", error);
        return {status: 500, error: error.message};
    }
}
// El pago parcial de una persona al dividir (José $30): la cuenta sigue abierta. Lo que completa el
// saldo se cobra con `collectRestaurantAccount`, que cierra.
function registerRestaurantAbono({saleId, amount, paymentMethod = null, label = null, accountId = null,
                                  approvalNumber = null, proof = null, tip = null}) {
    return _restaurantPaymentPost('/dashboard/restaurant/account/pay',
        {saleId, amount, paymentMethod, label, accountId, approvalNumber, tip}, proof);
}
// El cliente del negocio con esa cédula/RUC (o null), para facturar sin re-teclearlo.
function findRestaurantBuyer(identification) {
    return _restaurantPost('/dashboard/restaurant/buyer/find', {identification});
}
// «Cobrar»: cobra lo que falta (`payment`, los mismos campos que un abono), cierra la cuenta y la factura
// (o la archiva), en un solo paso. Sin `payment`: una cuenta que ya estaba cubierta.
function collectRestaurantAccount(saleId, mode = 'invoice', buyer = null, payment = null) {
    if (!payment) return _restaurantPost('/dashboard/restaurant/account/collect', {saleId, mode, buyer});
    const {amount, paymentMethod, label = null, accountId = null, approvalNumber = null, proof = null, tip = null} = payment;
    return _restaurantPaymentPost('/dashboard/restaurant/account/collect',
        {saleId, mode, buyer: buyer ? JSON.stringify(buyer) : null, amount, paymentMethod, label, accountId,
         approvalNumber, tip}, proof);
}
function moveRestaurantAccount(saleId, toTableId) {
    return _restaurantPost('/dashboard/restaurant/account/move', {saleId, toTableId});
}
// Cambia el nombre de una cuenta abierta («Cuenta 2» → «Grupo Ricardo»).
function renameRestaurantAccount(saleId, label) {
    return _restaurantPost('/dashboard/restaurant/account/rename', {saleId, label});
}
// Config del salón (admin): crear/editar y eliminar mesas.
function saveRestaurantTable(table) {
    return _restaurantPost('/dashboard/restaurant/table/save', table);
}
function deleteRestaurantTable(tableId) {
    return _restaurantPost('/dashboard/restaurant/table/delete', {tableId});
}
// Distribución del plano en lote (lo que el dueño arrastró): [{id, posX, posY}].
function saveRestaurantTableLayout(positions) {
    return _restaurantPost('/dashboard/restaurant/table/layout', {positions});
}
// Zonas del salón (entidad): crear / renombrar / reordenar ({id?, name?, order?}) y eliminar.
function saveRestaurantZone(zone) {
    return _restaurantPost('/dashboard/restaurant/zone/save', zone);
}
function deleteRestaurantZone(zoneId) {
    return _restaurantPost('/dashboard/restaurant/zone/delete', {zoneId});
}

// Purchase
async function getPurchases(title=null, start=null, end=null, paid=null, edoc=null, amount=null) {
    const data = {};
    if (title !== null && title !== "") { data.title = title }
    if (start !== null) { data.start = start }
    if (end !== null) { data.end = end }
    if (paid !== null) { data.paid = paid }
    if (edoc !== null) { data.edoc = edoc }
    if (amount !== null) { data.amount = amount }

    const response = await axios
        .post('/dashboard/purchase/get-all', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function getPurchase(purchaseId) {
    const data = {purchaseId: purchaseId};
    const response = await axios
        .post('/dashboard/purchase/get', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function savePurchase(purchase) {
    var data = {...purchase};
    data = JSON.stringify(data);
    const response = await axios
        .post('/dashboard/purchase/save', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Pregunta si una clave de acceso ya fue ingresada como compra. Se consulta al
// cargar un XML y antes de mandar al portal del SRI, para frenar el doble
// ingreso de la misma factura de proveedor.
// Responde {registered, purchase} dentro de `data`.
async function checkPurchaseAccessCode(accessCode) {
    const data = JSON.stringify({ accessCode });
    const response = await axios
        .post('/dashboard/purchase/access-code/check', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Productos para las líneas de una factura de este proveedor. `lines`: [{line, code,
// auxCode, description}]. Responde en `data`:
//   links: [{supplierCode, unitsPerSupplierUnit, supplierUnitPrice, product}], lo enlazado
//          la última vez; con eso el formulario arma los ítems de las líneas conocidas.
//   suggestions: [{line, confident, candidates: [{product, score, reason}]}], los productos
//          que más se parecen a las líneas sin enlace. Nunca se enlazan solas.
async function getSupplierProductLinks(vendorIdentification, lines) {
    const data = JSON.stringify({ vendorIdentification, lines });
    const response = await axios
        .post('/dashboard/purchase/supplier-links', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Dispara la descarga (async, vía Selenium en el backend) del XML de un
// comprobante recibido por su clave de acceso. El XML vuelve por WebSocket
// (tipo 'purchase_xml_result', correlacionado por requestId).
async function downloadPurchaseXML(accessCode, requestId) {
    const data = JSON.stringify({ accessCode, requestId });
    const response = await axios
        .post('/dashboard/purchase/xml/download', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function attemptToCreateRetention(purchaseId) {
    let data = {purchaseId: purchaseId};
    data = JSON.stringify(data);
    const response = await axios
        .post('dashboard/purchase/edoc/create', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function payPurchase(purchaseId) {
    const data = {purchaseId: purchaseId};
    const response = await axios
        .post('/dashboard/purchase/pay', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function deletePurchase(purchaseId, reportToSri = true) {
    const data = {purchaseId: purchaseId, reportToSri: reportToSri};
    const response = await axios
        .post('/dashboard/purchase/delete', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function downloadPurchase(purchaseId, type) {
    return downloadBlob('/dashboard/purchase/download-edoc', {purchaseId: purchaseId, type: type});
}

async function downloadPurchaseReport(format, initialDate, endDate) {
    return downloadBlob('/dashboard/purchase/download-report', {format: format, initialDate: initialDate, endDate: endDate});
}

// Remision Guide
async function getGuides() {
    const response = await axios
        .post('/dashboard/guide/get-all')
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function getGuide(guideId) {
    const data = {guideId: guideId};
    const response = await axios
        .post('/dashboard/guide/get', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function searchGuideDestination(searchText) {
    const data = {searchText: searchText};
    const response = await axios
        .post('/dashboard/guide/destinations/search', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function searchGuideCourrier(searchText) {
    const data = {searchText: searchText};
    const response = await axios
        .post('/dashboard/guide/courrier/search', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function saveGuide(guide) {
    var data = {...guide};
    data = JSON.stringify(data);
    const response = await axios
        .post('/dashboard/guide/save', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function attemptToCreateRemisionGuide(guideId, saleId) {
    var data = {guideId: guideId, saleId: saleId};
    data = JSON.stringify(data);
    const response = await axios
        .post('dashboard/guide/edoc/create', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// `reportToSri=false` solo cuando el usuario, sin credenciales del portal, elige
// anular igual: queda 'unreported' (ver dashboard/views.py::deleteRemisionGuide).
async function deleteGuide(guideId, reportToSri = true) {
    const data = {guideId: guideId, reportToSri: reportToSri};
    const response = await axios
        .post('/dashboard/guide/delete', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function downloadGuide(guideId, type) {
    return downloadBlob('/dashboard/guide/download', {guideId: guideId, type: type});
}

// Credit Notes
async function getCreditNotes(customer=null) {
    const data = {customer: customer};
    const response = await axios
        .post('/dashboard/credit-note/get-all', JSON.stringify(data),)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// Notas de crédito que el cliente puede canjear como pago (autorizadas, sin
// anular ni canjear). Va con el permiso de ventas: la usa quien cobra.
async function getAvailableCreditNotes(customerId) {
    const response = await axios
        .post('/dashboard/credit-note/available', JSON.stringify({customerId: customerId}))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function getCreditNote(id) {
    const data = {id: id};
    const response = await axios
        .post('/dashboard/credit-note/get', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function saveCreditNote(creditNote) {
    var data = {...creditNote};
    const response = await axios
        .post('/dashboard/credit-note/save', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}


async function attemptToCreateCreditNote(creditNoteId) {
    var data = {id: creditNoteId};
    data = JSON.stringify(data);
    const response = await axios
        .post('dashboard/credit-note/edoc/create', data)
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

// `reportToSri=false` solo cuando el usuario, sin credenciales del portal, elige
// anular igual: queda 'unreported' (ver dashboard/views.py::deleteCreditNote).
async function deleteCreditNote(creditNoteId, reportToSri = true) {
    const data = {creditNoteId: creditNoteId, reportToSri: reportToSri};
    const response = await axios
        .post('/dashboard/credit-note/delete', JSON.stringify(data))
        .then(response => { return response.data; })
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        })
    return response
}

async function downloadCreditNote(creditNoteId, type) {
    return downloadBlob('/dashboard/credit-note/download', {creditNoteId: creditNoteId, type: type});
}

// Cash Closing (Cierre de Caja)
async function getCashClosingPreview() {
    const response = await axios
        .post('/dashboard/cash-closing/preview', JSON.stringify({}))
        .then(r => r.data)
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        });
    return response;
}

async function saveCashClosing(lines, notes) {
    const data = { lines: lines, notes: notes || "" };
    const response = await axios
        .post('/dashboard/cash-closing/save', JSON.stringify(data))
        .then(r => r.data)
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        });
    return response;
}

async function getCashClosings(filters) {
    const data = filters || {};
    const response = await axios
        .post('/dashboard/cash-closing/get-all', JSON.stringify(data))
        .then(r => r.data)
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        });
    return response;
}

async function getCashClosing(id) {
    const response = await axios
        .post('/dashboard/cash-closing/get', JSON.stringify({ id: id }))
        .then(r => r.data)
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        });
    return response;
}

async function voidCashClosing(id, reason) {
    const response = await axios
        .post('/dashboard/cash-closing/void', JSON.stringify({ id: id, reason: reason || "" }))
        .then(r => r.data)
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        });
    return response;
}

async function saveCashClosingRecipients(recipients) {
    const response = await axios
        .post('/dashboard/business/activity/cash-closing-recipients/save',
              JSON.stringify({ recipients: recipients || "" }))
        .then(r => r.data)
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        });
    return response;
}

// Inventario: conteo a ciegas, sincronización de stock y bitácora de ajustes
async function postInventory(path, data) {
    return axios
        .post(path, JSON.stringify(data || {}))
        .then(r => r.data)
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        });
}

async function getInventoryCountPreview(categoryId) {
    return postInventory('/dashboard/inventory-count/preview', { categoryId: categoryId || null });
}

async function saveInventoryCount(lines, notes, categoryId, sessionToken) {
    return postInventory('/dashboard/inventory-count/save', {
        lines: lines, notes: notes || "", categoryId: categoryId || null, sessionToken: sessionToken || null
    });
}

async function getInventoryCounts(filters) {
    return postInventory('/dashboard/inventory-count/get-all', filters);
}

async function getInventoryCount(id) {
    return postInventory('/dashboard/inventory-count/get', { id: id });
}

async function getInventoryCountApplyPreview(id) {
    return postInventory('/dashboard/inventory-count/apply/preview', { id: id });
}

async function applyInventoryCount(id, lineIds, reasonCodes, notes) {
    return postInventory('/dashboard/inventory-count/apply', {
        id: id, lineIds: lineIds, reasonCodes: reasonCodes || {}, notes: notes || ""
    });
}

async function voidInventoryCount(id, reason) {
    return postInventory('/dashboard/inventory-count/void', { id: id, reason: reason || "" });
}

async function getSyncableProducts(filter, productId) {
    return postInventory('/dashboard/stock-sync/products', { filter: filter || null, productId: productId || null });
}

async function saveStockSync(productId, physicalStock, reason, reasonCode) {
    return postInventory('/dashboard/stock-sync/save', {
        productId: productId, physicalStock: physicalStock, reason: reason || "", reasonCode: reasonCode || null
    });
}

async function getStockAdjustments(filters) {
    return postInventory('/dashboard/stock-adjustment/get-all', filters);
}

async function getStockAdjustmentSummary(filters) {
    return postInventory('/dashboard/stock-adjustment/summary', filters);
}

// Lista de compras: lo que quedó debajo de su nivel par, por proveedor.
async function getShoppingList() {
    return postInventory('/dashboard/inventory/shopping-list', {});
}

// ============================================================
// Módulo Contable (Accounting)
// ============================================================

async function _accountingPost(url, body) {
    const response = await axios
        .post(url, JSON.stringify(body || {}))
        .then(r => r.data)
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        });
    return response;
}

// Ciclo de activación (R8)
async function getAccountingStatus() {
    return _accountingPost('/dashboard/contabilidad/estado');
}

async function enableAccounting() {
    return _accountingPost('/dashboard/contabilidad/habilitar');
}

async function disableAccounting() {
    return _accountingPost('/dashboard/contabilidad/deshabilitar');
}

async function getAccountingOpeningPreview() {
    return _accountingPost('/dashboard/contabilidad/apertura/preview');
}

async function confirmAccountingOpening(overrides) {
    return _accountingPost('/dashboard/contabilidad/apertura/confirmar', { overrides: overrides || {} });
}

async function resetAccountingOpening() {
    return _accountingPost('/dashboard/contabilidad/apertura/reset');
}

async function goLiveAccounting() {
    return _accountingPost('/dashboard/contabilidad/go-live');
}

// Reportes
async function getAccountingIncomeStatement(start, end) {
    return _accountingPost('/dashboard/contabilidad/estado-resultados', { start: start, end: end });
}

async function getAccountingBalanceSheet(asOf) {
    return _accountingPost('/dashboard/contabilidad/balance-general', { asOf: asOf });
}

// Plan de cuentas y mapeos (V2.D)
async function getAccountingChart() {
    return _accountingPost('/dashboard/contabilidad/plan-cuentas');
}

// Edición del plan de cuentas (V2-8). No hay borrar: una cuenta con asientos es
// PROTECT en el backend; se desactiva.
async function createAccountingAccount(parentId, name) {
    return _accountingPost('/dashboard/contabilidad/plan-cuentas/crear',
                           { parentId: parentId, name: name });
}

async function renameAccountingAccount(accountId, name) {
    return _accountingPost('/dashboard/contabilidad/plan-cuentas/renombrar',
                           { accountId: accountId, name: name });
}

async function setAccountingAccountActive(accountId, active) {
    return _accountingPost('/dashboard/contabilidad/plan-cuentas/estado',
                           { accountId: accountId, active: active });
}

async function getAccountingMappings() {
    return _accountingPost('/dashboard/contabilidad/mapeos');
}

async function remapAccountingTreasury(finantialAccountId, accountId) {
    return _accountingPost('/dashboard/contabilidad/mapeos/tesoreria',
                           { finantialAccountId: finantialAccountId, accountId: accountId });
}

async function mapAccountingProductCategory(categoryId, role, accountId) {
    return _accountingPost('/dashboard/contabilidad/mapeos/categoria',
                           { categoryId: categoryId, role: role, accountId: accountId });
}

async function getPurchaseExpenseCategories() {
    return _accountingPost('/dashboard/purchase/expense-categories');
}

async function mapAccountingExpenseType(expenseType, accountId) {
    return _accountingPost('/dashboard/contabilidad/mapeos/gasto',
                           { expenseType: expenseType, accountId: accountId });
}

// «Quitar mapeo» de una categoría de egreso (H67).
async function unmapAccountingExpenseType(expenseType) {
    return _accountingPost('/dashboard/contabilidad/mapeos/gasto/quitar', { expenseType: expenseType });
}

async function getAccountingTaxSummary(year, month) {
    return _accountingPost('/dashboard/contabilidad/resumen-iva', { year: year, month: month });
}

async function getAccountingTrialBalance(asOf) {
    return _accountingPost('/dashboard/contabilidad/balance-comprobacion', { asOf: asOf });
}

async function getAccountingAccountLedger(code) {
    return _accountingPost('/dashboard/contabilidad/balance-comprobacion/detalle', { code: code });
}

async function getAccountingReconciliation() {
    return _accountingPost('/dashboard/contabilidad/reconciliacion');
}

// «Generar asientos faltantes» (H47): repone lo que una cola perdida dejó sin asiento.
async function repostAccountingMissing() {
    return _accountingPost('/dashboard/contabilidad/salud/asientos-faltantes');
}

// Retenciones
// Retenciones que te practican los clientes: captura manual o desde el XML
async function registerSaleRetention(payload) {
    return _accountingPost('/dashboard/contabilidad/retenciones-recibidas', payload);
}

async function uploadSaleRetentionXml(file, saleId) {
    const form = new FormData();
    form.append('file', file);
    if (saleId) form.append('saleId', saleId);
    const response = await axios
        .post('/dashboard/contabilidad/retenciones-recibidas/xml', form,
              { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(r => r.data)
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        });
    return response;
}

async function matchSettlementRow(rowId, transactionId) {
    return _accountingPost('/dashboard/contabilidad/conciliacion/emparejar',
                           { rowId: rowId, transactionId: transactionId });
}

async function unmatchSettlementRow(rowId) {
    return _accountingPost('/dashboard/contabilidad/conciliacion/desemparejar',
                           { rowId: rowId });
}

// Activos fijos: se capitalizan y se deprecian, no son inventario ni gasto
async function getAccountingFixedAssets() {
    return _accountingPost('/dashboard/contabilidad/activos');
}

async function createAccountingFixedAsset(asset) {
    return _accountingPost('/dashboard/contabilidad/activos/crear', asset);
}

async function disposeAccountingFixedAsset(disposal) {
    return _accountingPost('/dashboard/contabilidad/activos/baja', disposal);
}

async function runAccountingDepreciation(year, month) {
    return _accountingPost('/dashboard/contabilidad/activos/depreciar',
                           { year: year, month: month });
}

// Liquidaciones: pasivos que el motor acumula y alguien tiene que drenar
async function getAccountingLiabilities() {
    return _accountingPost('/dashboard/contabilidad/pasivos');
}

async function payAccountingIce(accountId, amount) {
    return _accountingPost('/dashboard/contabilidad/ice/pagar',
                           { accountId: accountId, amount: amount });
}

async function payAccountingTips(accountId, amount) {
    return _accountingPost('/dashboard/contabilidad/propinas/pagar',
                           { accountId: accountId, amount: amount });
}

async function payAccountingRetentions(accountId, ivaAmount, rentAmount) {
    return _accountingPost('/dashboard/contabilidad/retenciones/pagar',
                           { accountId: accountId, ivaAmount: ivaAmount, rentAmount: rentAmount });
}

// Conciliación de pasarelas (R9)
async function importSettlement(file, acquirer) {
    const form = new FormData();
    form.append('file', file);
    form.append('acquirer', acquirer || 'DATAFAST');
    const response = await axios
        .post('/dashboard/contabilidad/conciliacion/importar', form,
              { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(r => r.data)
        .catch(error => {
            console.error("There was an error!", error);
            return {status: 404}
        });
    return response;
}

async function confirmSettlement(batchId, bankAccountId) {
    return _accountingPost('/dashboard/contabilidad/conciliacion/confirmar',
                           { batchId: batchId, bankAccountId: bankAccountId });
}

async function getSettlementBatches(batchId) {
    return _accountingPost('/dashboard/contabilidad/conciliacion/batches',
                           batchId ? { batchId: batchId } : {});
}

export default {
    // Account
    syncAccount,
    getBusinessMetrics,
    getFinantialStateData,
    getGrossMarginData,
    getRecentSales,
    getTopProducts,
    getReceivablesPayables,
    getReceivablesPayablesDetail,
    // Authentication
    login,
    logout,
    getSession,
    getScreenProfile,
    listSubUsers,
    getWorkSchedules,
    saveWorkSchedule,
    deleteWorkSchedule,
    assignWorkSchedule,
    saveScheduleException,
    deleteScheduleException,
    createSubUser,
    updateSubUser,
    noteEntitlementsVersion,
    updatePassword,
    requestPasswordReset,
    confirmPasswordReset,
    // Business
    getBusiness,
    saveBusiness,
    getEconActivities,
    saveEconActivity,
    saveEstablishment,
    deleteEstablishment,
    saveEmitionPoint,
    deleteEmitionPoint,
    setDefaultEconActivity,
    saveDefaults,
    saveUserSettings,
    // Service Plans
    getServicePlans,
    getPlanCatalogue,
    // Subscriptions
    getSubscriptions,
    renewSubscription,
    // Device,
    getDevices,
    selectDevice,
    // Banks
    getBanks,
    getPaymentGateways,
    // Finantial Accounts
    getFinantialAccounts,
    getFinantialAccount,
    saveFinantialAccount,
    deleteFinantialAccount,
    createFinantialAccountTransactionsBatch: createMultipleTransactions,
    createMultipleTransactions,
    registerPayment,
    registerBulkPayment,
    getFinantialAccountTransaction,
    // Message & Broadcast
    getBroadcastMessage,
    getBroadcastMessages,
    seenBroadcastMessage,
    // Customer
    getCustomers,
    saveCustomer,
    deleteCustomers,
    downloadClients,
    // Product
    getProducts,
    saveProduct,
    deleteProducts,
    downloadProducts,
    getProductSalesReport,
    getProductKardex,
    generateProductKardexReport,
    saveProductStockAdjustment,
    getProductCompositions,
    saveProductCompositions,
    deleteProductComposition,
    // Providers
    getProviders,
    saveProvider,
    deleteProviders,
    // Balance
    getBalance,
    printBalance,
    // Proformas
    getProformas,
    getProforma,
    saveProforma,
    sendProformaEmail,
    downloadProformaFile,
    deleteProforma,
    // Documents
    signPdfDocument,
    // Sales
    getSales,
    getSalesPage,
    getSale,
    saveSale,
    collectSale,
    printSale,
    checkAnnulmentStatus,
    reportAnnulmentToSri,
    deleteSale,
    downloadTaxSummary,
    downloadSales,
    downloadSaleEdoc,
    downloadSalePDF,
    // Restaurante (Mesas)
    getRestaurantFloor,
    getRestaurantTableDetail,
    openRestaurantAccount,
    addRestaurantItems,
    removeRestaurantItem,
    sendRestaurantToKitchen,
    registerRestaurantAbono,
    findRestaurantBuyer,
    collectRestaurantAccount,
    moveRestaurantAccount,
    renameRestaurantAccount,
    saveRestaurantTable,
    deleteRestaurantTable,
    saveRestaurantTableLayout,
    saveRestaurantZone,
    deleteRestaurantZone,
    // Purchases
    getPurchases,
    getPurchase,
    savePurchase,
    attemptToCreateRetention,
    payPurchase,
    deletePurchase,
    downloadPurchase,
    downloadPurchaseReport,
    downloadPurchaseXML,
    checkPurchaseAccessCode,
    getSupplierProductLinks,
    // Remision Guide
    getGuides,
    getGuide,
    searchGuideDestination,
    searchGuideCourrier,
    saveGuide,
    attemptToCreateRemisionGuide,
    deleteGuide,
    downloadGuide,
    // Credit Note
    getCreditNotes,
    getCreditNote,
    getAvailableCreditNotes,
    saveCreditNote,
    attemptToCreateCreditNote,
    deleteCreditNote,
    downloadCreditNote,
    // Cash Closing
    getCashClosingPreview,
    saveCashClosing,
    getCashClosings,
    getCashClosing,
    voidCashClosing,
    saveCashClosingRecipients,
    // Inventario
    getInventoryCountPreview,
    saveInventoryCount,
    getInventoryCounts,
    getInventoryCount,
    getInventoryCountApplyPreview,
    applyInventoryCount,
    voidInventoryCount,
    getSyncableProducts,
    saveStockSync,
    getStockAdjustments,
    getStockAdjustmentSummary,
    getShoppingList,
    // Accounting (Módulo Contable)
    getAccountingStatus,
    enableAccounting,
    disableAccounting,
    getAccountingOpeningPreview,
    confirmAccountingOpening,
    resetAccountingOpening,
    goLiveAccounting,
    getAccountingIncomeStatement,
    getAccountingTaxSummary,
    getAccountingChart,
    createAccountingAccount,
    renameAccountingAccount,
    setAccountingAccountActive,
    getAccountingMappings,
    remapAccountingTreasury,
    mapAccountingExpenseType,
    unmapAccountingExpenseType,
    getPurchaseExpenseCategories,
    mapAccountingProductCategory,
    getProductCategories,
    saveProductCategory,
    getAccountingBalanceSheet,
    getAccountingTrialBalance,
    getAccountingAccountLedger,
    getAccountingReconciliation,
    repostAccountingMissing,
    payAccountingRetentions,
    getAccountingLiabilities,
    registerSaleRetention,
    uploadSaleRetentionXml,
    matchSettlementRow,
    unmatchSettlementRow,
    getAccountingFixedAssets,
    createAccountingFixedAsset,
    disposeAccountingFixedAsset,
    runAccountingDepreciation,
    payAccountingIce,
    payAccountingTips,
    importSettlement,
    confirmSettlement,
    getSettlementBatches,
}